/**
 * Native offline indicator for the Capacitor shell.
 *
 * The shell loads the live web app over the network, so when connectivity drops
 * there's no native cue (unlike a real offline-aware app). This shows a small
 * themed banner while offline and removes it on reconnect.
 *
 * The banner is injected as a plain DOM element (not React) with inline styles,
 * so it works even if the React app hasn't mounted (e.g. the network dropped
 * during the initial load). No-op outside the Capacitor shell (browser +
 * MobiLoud keep their own behavior) and never throws.
 *
 * @capacitor/network is loaded lazily (dynamic import) so it is excluded from
 * the main entry bundle and never downloaded/parsed in MobiLoud or plain web —
 * only fetched when isCapacitorEnvironment() is true.
 */

import Honeybadger from "@honeybadger-io/js";
import {
  isCapacitorEnvironment,
  isCapacitorPluginAvailable,
} from "./detection";
import { reloadWebApp } from "./reload_page";
import { hasActiveUploads } from "../utils/upload_activity";

const BANNER_ID = "tkc-offline-banner";

// After a real outage, reload on reconnect so a page left stuck mid-request
// (spinners / "couldn't load" errors) recovers without a manual kill+restart.
// Guarded by a minimum offline duration so momentary blips (elevator, transit)
// don't reload and interrupt the user.
const OFFLINE_RELOAD_MIN_MS = 3000;
let offlineSince = null;

function reportOfflineError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_offline" },
    });
  } catch {
    // reporting must never break the app
  }
}

// A neutral dark bar (MUI-snackbar style) so it's legible in both light and
// dark themes without having to track the theme. Pinned to the bottom, above
// the home-indicator safe area.
function ensureBanner() {
  const existing = document.getElementById(BANNER_ID);
  if (existing) {
    return existing;
  }
  const el = document.createElement("div");
  el.id = BANNER_ID;
  el.textContent = "You're offline";
  el.setAttribute("role", "status");
  el.style.cssText = [
    "position:fixed",
    "left:0",
    "right:0",
    "bottom:0",
    "z-index:2147483647",
    "padding:10px 12px calc(10px + env(safe-area-inset-bottom))",
    "text-align:center",
    "color:#fff",
    "background:#323232",
    "font:600 14px/1.3 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    "box-shadow:0 -1px 6px rgba(0,0,0,0.25)",
  ].join(";");
  document.body.appendChild(el);
  return el;
}

function showOfflineBanner() {
  ensureBanner().style.display = "block";
}

function hideOfflineBanner() {
  const el = document.getElementById(BANNER_ID);
  if (el) {
    el.style.display = "none";
  }
}

// Reflect a status object from @capacitor/network: hide when connected, show
// the banner when not.
function applyStatus(status) {
  if (status && status.connected) {
    hideOfflineBanner();
    // Reconnected: recover a page that failed while offline, but only after a
    // real outage (ignore momentary blips) and only if we were actually offline.
    if (
      offlineSince != null &&
      Date.now() - offlineSince > OFFLINE_RELOAD_MIN_MS
    ) {
      offlineSince = null;
      // An in-flight upload proves the page is alive and mid-work — and the
      // image Uploader may be paused waiting for THIS reconnect to resume
      // (uploader.js offline pause). Reloading now would destroy the transfer
      // the moment it becomes able to finish; the stuck-page recovery this
      // reload exists for doesn't apply to a page actively uploading.
      if (hasActiveUploads()) {
        return;
      }
      reloadWebApp();
      return;
    }
    offlineSince = null;
  } else {
    if (offlineSince == null) {
      offlineSince = Date.now();
    }
    showOfflineBanner();
  }
}

let watching = false;

/**
 * Fallback connectivity watcher using browser window events. Used inside the
 * Capacitor shell when @capacitor/network is not registered on the current
 * platform (e.g. Android before the native plugin is installed).
 *
 * navigator.onLine is the initial state; "online"/"offline" events update it.
 * This is lower-fidelity than the Capacitor plugin (it only tracks basic
 * network reachability, not connection type) but it stops the 171 unhandled
 * "Network plugin is not implemented on Android" errors.
 */
function watchWithBrowserEvents() {
  applyStatus({ connected: navigator.onLine });
  window.addEventListener("online", () => {
    applyStatus({ connected: true });
  });
  window.addEventListener("offline", () => {
    applyStatus({ connected: false });
  });
}

/**
 * Start watching connectivity and toggling the offline banner. No-op off-shell;
 * runs once; never throws.
 *
 * Uses @capacitor/network when the plugin is registered in the native shell.
 * Falls back to browser window online/offline events on platforms where the
 * plugin is not available (e.g. Android before @capacitor/network is added to
 * the native project) — this prevents "Network plugin is not implemented on
 * Android" errors from surfacing in Honeybadger.
 */
export function initOfflineWatch() {
  if (!isCapacitorEnvironment() || watching) {
    return;
  }
  watching = true;

  if (!isCapacitorPluginAvailable("Network")) {
    // Plugin not registered on this platform (currently Android). Fall back to
    // browser events so the banner still works without throwing.
    watchWithBrowserEvents();
    return;
  }

  import("@capacitor/network")
    .then(({ Network }) => {
      // Reflect the current status on launch (offline-at-start shows the banner).
      Network.getStatus().then(applyStatus).catch(reportOfflineError);
      // Network.addListener is async; surface a rejection instead of leaving it as
      // an unhandled promise rejection. A sync throw resets the guard to retry.
      try {
        const result = Network.addListener("networkStatusChange", applyStatus);
        if (result && typeof result.catch === "function") {
          result.catch(reportOfflineError);
        }
      } catch (error) {
        watching = false;
        reportOfflineError(error);
      }
    })
    .catch((error) => {
      watching = false;
      reportOfflineError(error);
    });
}
